<template>
  <li
    :data-qa="`service-product-list-item`"
    :data-label="item.Name"
    class="item"
    :disabled="disabled || null"
    :class="{ 'is-active': isSelected }"
    data-action="add to list"
    data-eventcategory="service flow"
  >
    <div class="content">
      <div class="header">
        <div
          class="title"
          data-qa="service-product-title"
          @click="
            titleClickTogglesItem || !showProductDescription ? toggleItem() : productNameClick()
          "
        >
          {{ item.Name }}
        </div>

        <span v-if="showPurchasePrice" class="price" data-qa="service-product-price">
          {{ i18n.n(item.Price.PurchaseExcl, 'currency') }}
        </span>
        <span v-else class="price" data-qa="service-product-price">
          {{ priceText }}
        </span>

        <button
          class="add"
          data-qa="service-product-toggle-button"
          @click="showProductDescription ? toggleItem() : null"
        >
          <span class="sr-only">
            {{ item.name }}
            <span>{{ i18n.t('/ProductBlocks/AddToSelection') }}</span>
          </span>
          <icon v-if="isSelected" class="icon-add" icon="check-bold" />
        </button>
      </div>
      <div v-if="showDescription" data-qa="service-product-description" class="description">
        {{ item.Notes }}
      </div>
      <package :item="item" :part-of-package="partOfPackage" />
    </div>
  </li>
</template>

<script>
import Icon from '@/components/generic/presentational/atoms/icon/Icon.vue'
import Package from '@/components/generic/presentational/molecules/package/Package.vue'

export default {
  components: {
    Icon,
    Package,
  },
  // Flag for adding expandable product description, if true then title will expand/unexpand description and toggle only when clicking icon
  inject: {
    showProductDescription: {
      from: 'showProductDescription',
      default: false,
    },
    titleClickTogglesItem: {
      from: 'titleClickTogglesItem',
      default: false,
    },
  },
  props: {
    item: {
      type: Object,
      default: () => {},
    },
    selectedProducts: {
      type: Array,
      default: () => [],
    },
    showPurchasePrice: {
      type: Boolean,
      default: false,
    },
    isSelected: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    showExclPrice: {
      type: Boolean,
      default: false,
    },
    showInclPrice: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['product-name-click', 'remove-from-selected', 'add-to-selected'],
  data() {
    return {
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
      showDescription: true,
    }
  },
  computed: {
    partOfPackage() {
      const product = this.selectedProducts.find((product) => {
        return this.item.PartOfPackage !== null && product.OptionType_ID === this.item.PartOfPackage
      })
      if (product) {
        return product
      }
      return null
    },
    priceText() {
      let priceValue =
        this.showExclPrice && !this.showInclPrice ? this.item.Price.Excl : this.item.Price.Incl
      return this.i18n.n(priceValue, 'currency')
    },
  },
  methods: {
    toggleItem() {
      this.$emit(this.isSelected ? 'remove-from-selected' : 'add-to-selected', this.item)
    },
    productNameClick() {
      if (this.item.ID.length > 6) {
        this.$emit('product-name-click', this.item.ID)
      } else {
        this.showDescription = !this.showDescription
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@include sr-only;

.item {
  $hover-animation-timer: 0.2s;
  margin-bottom: 0.5em;

  border: 1px solid $gray-lighter;
  cursor: pointer;
  position: relative;
  transition:
    box-shadow $hover-animation-timer ease-in-out,
    background-color $hover-animation-timer ease-in-out;
  overflow: hidden;

  &:hover {
    box-shadow: 0 0 2em rgba(0, 0, 0, 0.2);
  }

  &::before {
    transition: all ease-in;
    position: absolute;
    right: 0;
    top: 0;
    content: '';
    background: $color-primary-light;
    transition-duration: 0.2s;
    width: 3.5em;
    height: 100%;
  }

  &.is-active {
    border-color: $color-primary;
    &::before {
      transition-timing-function: ease-out;
      width: 120%;
      height: 120%;
      border-bottom-left-radius: 50%;
    }
  }

  &[disabled] {
    &::before {
      background-color: #f1f4f5;
    }

    border-color: #dae2e5;
  }
}

.header {
  display: flex;
  flex-direction: row;
}

.content {
  position: relative;
  padding: $gutter / 2;

  @include breakpoint(md) {
    padding-left: $gutter;
    padding-right: $gutter;
  }
}

.description {
  font-size: $font-size-base * 0.8;
}

.title {
  flex: 1 1 auto;
  margin-right: 1em;
}

.price {
  display: block;
  font-weight: $font-weight-bold;
  margin-right: 1.5em;
  flex: 0 0 auto;
  text-align: right;
}

.add {
  border-radius: 50%;
  width: 1.5em;
  height: 1.5em;
  flex: 0 0 auto;
  border: 1px solid $color-primary;
  color: $white;
  background-color: $white;

  .is-active & {
    background-color: $color-primary;
  }

  [disabled] & {
    background-color: #a3b8c0;
    border-color: #dae2e5;
  }
}

.icon-add {
  @include icon(0.75em);
}
</style>
